import {
  EMPTY_ADDRESS,
  EMPTY_EXTENDED_ADDRESS
} from '@dadohr/shared/domainTypes/address';
import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  BatchMode,
  ChannelType,
  ContentTemplate,
  ContentType,
  CREATE_MEETING_SERVICES,
  ExperienceTemplateMessage,
  IExperienceContactMail,
  IExperienceMessageDenormalized,
  isContentBlock,
  isEventDrivenTiming,
  isLayoutBlock,
  isSubTaskBlock,
  ISubtaskCreateMeetingDenormalized,
  ISubTaskDataFieldChangeDenormalized,
  ISubTaskDocuSignEnvelopeSignDenormalized,
  ISubTaskQuestionDenormalized,
  ISubTaskSwagUpChoiceDenormalized,
  ISupportingActor,
  ITaskDenormalized,
  ITaskTemplate,
  LayoutBlock,
  LayoutBlockType,
  MessageStatus,
  Audience,
  PARTICIPANT_ACTOR_KEY,
  QuestionType,
  SubTaskDenormalized,
  SubTaskStatus,
  SubTaskType,
  SupportingActorType,
  TaskBlockDenormalized,
  TaskStatus,
  TextQuestionValidationType
} from '@dadohr/shared/domainTypes/experience';
import {
  IOrganization,
  IOrganizationDenormalized
} from '@dadohr/shared/domainTypes/org';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import assertNever from 'assert-never';
import { capitalize, mapValues } from 'lodash';
import {
  AlertCircle,
  Calendar,
  Check,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  GripVertical,
  Image,
  Link as LinkIcon,
  Plus,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import {
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable
} from 'react-beautiful-dnd';
import { Img } from 'react-image';
import { SubTaskBackgroundCheckInvite } from '../../../../../../01-standalone-app/features/Experience/components/subTasks/SubTaskBackgroundCheckInvite';
import { SwagUpChoiceSelector } from '../../../../../../01-standalone-app/features/Experience/components/subTasks/SubTaskSwagUpChoice';
import {
  CalloutContainer,
  ColumnsContainer
} from '../../../../../../01-standalone-app/features/Experience/components/Task';
import {
  AddressExtendedForm,
  AddressForm
} from '../../../../../../components/AddessForm';
import { ColoredChipLight } from '../../../../../../components/ColoredChip';
import {
  CompleteButton,
  CompleteLink
} from '../../../../../../components/CompletionButtons';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { DocumentTeaserWithPreview } from '../../../../../../components/DocumentTeaser';
import { FormRow } from '../../../../../../components/Form/FormRow';
import {
  LinearScaleInput,
  RatingInput
} from '../../../../../../components/ScaleQuestion';
import { ImageContainer } from '../../../../../../components/ImageContainer';
import { InfoBox } from '../../../../../../components/InfoBox';
import { MailTo } from '../../../../../../components/MailTo';
import {
  MarkdownText,
  MarkdownTextWithReplacementPlaceholders,
  PlainTextWithReplacementPlaceholders,
  Token,
  TokenDefinition
} from '../../../../../../components/MarkdownText';
import { VideoPlayer } from '../../../../../../components/VideoPlayer';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { Centered } from '../../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import {
  getAudienceById,
  getAudienceName,
  getEveryoneAudience
} from '../../../../../../services/experiences/audience';
import { toDenormalizedMessage } from '../../../../../../services/experiences/instance';
import {
  approximateIfAggregationForMessageIsPossible,
  getAnchorInterpolationVariable,
  templateTimingsShareTriggerCondition
} from '../../../../../../services/experiences/template';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { toDenormalizedOrg } from '../../../../../../services/orgs';
import { InterpolationDefaultOptions } from '../../../../../../services/set/interpolation';
import {
  now,
  ONE_DAY,
  printTz,
  toWeekAndDayDuration
} from '../../../../../../services/time';
import { truncateUrl } from '../../../../../../services/url';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useDoceboConfig } from '../../../../../../features/Org/pages/Integrations/integrations/docebo/service';
import { useTheme } from '../../../../../../themes';
import { AppThemeProviderWithOptionalCustomizations } from '../../../../../../themes/provider';
import { AnchorField, useAnchorFields } from '../../../Anchors';
import { TaskStatusChip } from '../../../TaskStatusChip';
import { useGoogleCalendarEventInvitiationAnchorOptions } from '../../AutomationBuilder/ActionSettings/GoogleCalendar';
import { actionActorToString } from '../../AutomationBuilder/shared';
import {
  CONTROLS_CONTAINER_CLASS_NAME,
  DragDropItemContainer,
  DragDropItemControlsButton,
  DragDropItemControlsContainer,
  DragDropItemFlag,
  DragDropItemFlagsContainer,
  DragDropListContainer,
  DragDropListEmpty
} from '../../components/DragDropList';
import {
  MessageBuilderViewMode,
  MinimalTaskBuilderContext,
  TaskBuilderContext
} from '../types';
import { TaskBuilderBlockValidationState } from '../validation';
import { DEFAULT_AUDIENCE_COLOR } from './Audiences';
import { isValidColumnContent } from './BlockSettings/Layout/Columns';
import { DROP_IDS } from './dndIds';

export const MessageContainer = styled('div')((p) => ({
  maxWidth: 600,
  width: '100%',
  margin: '0 auto'
}));

const MessageHeaderContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: p.theme.spacing(2),
  gap: p.theme.spacing()
}));

export const MessageBody = styled('div')((p) => ({
  padding: p.theme.spacing(2)
}));

export const MessageMainContainer = styled('div')((p) => ({
  border: p.theme.custom.border.standard,
  background: p.theme.custom.colors.gray.white,
  borderTop: `${p.theme.spacing(0.5)}px solid ${p.theme.palette.primary.main}`
}));

const MessageFooter1 = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(2)}px`,
  background: p.theme.custom.colors.gray.w200,
  gap: p.theme.spacing()
}));

const MessageFooter2 = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${p.theme.spacing(1)}px ${p.theme.spacing(2)}px`,
  color: p.theme.custom.colors.gray.white,
  background: p.theme.custom.colors.gray.w900,
  fontSize: p.theme.custom.fontSize.s,
  gap: p.theme.spacing()
}));

const Greeting = ({
  tokens,
  actor,
  salutations
}: {
  tokens: TokenDefinition[];
  actor: string;
  salutations: { [actorKey: string]: string };
}) => {
  const { t } = useTranslation();
  const salutation = salutations?.[actor];
  const { color, group, label } = tokens.find(
    (tk) => tk.token === `${actor}.firstName`
  ) || {
    color: 'blue',
    group: 'Participant',
    label: 'First Name'
  };
  return (
    <Typography component="h1" variant="h5">
      {!salutation && (
        <>
          {t('greetings.hi')}{' '}
          <Token color={color}>
            {group} {group && <>:</>} {label}
          </Token>
          !
        </>
      )}
      {!!salutation && (
        <MarkdownText
          t={{
            t: salutation,
            textAlign: 'left',
            v: 1
          }}
        />
      )}
    </Typography>
  );
};

const ViewYourTasksPageButton = () => {
  const { t } = useTranslation();
  return (
    <Button variant="outlined" color="default" size="small">
      {t('mail.goToYourPage')}
    </Button>
  );
};

const HelpContainer = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.s
}));

const Help = ({
  tpl,
  subjectFragment,
  tokens
}: {
  tpl: MinimalTaskBuilderContext;
  subjectFragment: string;
  tokens: TokenDefinition[];
}) => {
  const { t } = useTranslation();
  const mailContact = tpl.contacts.find((c) => c.type === 'MAIL') as
    | IExperienceContactMail
    | undefined;
  if (!mailContact) {
    return null;
  }
  return (
    <HelpContainer>
      <MailTo
        email={mailContact.email}
        subject={`[${tpl.title}] ${subjectFragment}`}
      >
        <PlainTextWithReplacementPlaceholders
          tokens={tokens}
          text={`${t('mail.needHelp')} ${t('mail.reachOut')} ${
            mailContact.name || mailContact.email
          }`}
        />
      </MailTo>
    </HelpContainer>
  );
};

const MessageFrameInfoBoxContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2),
  marginBottom: p.theme.spacing(2)
}));

export const MessageHeader = ({ logoUrl }: { logoUrl: string }) => {
  return (
    <MessageHeaderContainer>
      <img
        width={102}
        src={logoUrl || '/images/dado-logo/dado-logo.svg'}
        alt=""
        onLoad={(ev: any) => {
          try {
            // Matches the behavior in emails, where the aspect
            // ratio is precomputed.
            const img: HTMLImageElement = ev.target;
            const aspect = img.width / img.height;
            if (aspect > 5) {
              img.width = 204;
            }
          } catch {}
        }}
      />
    </MessageHeaderContainer>
  );
};

export const MessageSentByDadoFooter = () => {
  const { t } = useTranslation();
  return (
    <MessageFooter2>
      <div>{t('mail.sentByDado')}</div>
      <img
        src={'/images/dado-logo/dado-logo-reverse.svg'}
        alt="Dado"
        height={26 * 0.6}
        width={102 * 0.6}
      />
    </MessageFooter2>
  );
};

/**
 * NEW_UX persistent affordance at the TOP of the preview sheet body — a subtle grey
 * bar that reminds the admin where blocks come from. On the Everyone / all-blocks tab
 * you can only add fresh blocks (Content panel); on a segment tab you can also pull an
 * existing block in from the All blocks tab.
 */
export const AddBlocksHintBar: React.FC<{ onEveryoneTab?: boolean }> = ({
  onEveryoneTab = true
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div
      style={{
        borderBottom: '1px solid #F0F0F0',
        padding: '14px 16px',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: '#494F55',
        fontSize: 14,
        fontWeight: 200
      }}
    >
      <Plus size={14} />
      {onEveryoneTab
        ? t('Add blocks from the Content panel')
        : t('Add blocks from the Content panel or the All blocks tab')}
    </div>
  );
};

const MessageFrame: React.FC<{
  subjectFragment: string;
  expTpl: MinimalTaskBuilderContext;
  org: IOrganizationDenormalized;
  infoBox?: React.ReactNode;
  hidePreview?: boolean;
  hideViewYourPageButton?: boolean;
  tokens: TokenDefinition[];
  isSlackPreview?: boolean;
  // NEW_UX: rendered full-bleed after the block body, before the footer.
  addBlocksHint?: React.ReactNode;
}> = ({
  subjectFragment,
  expTpl,
  org,
  infoBox,
  hidePreview,
  hideViewYourPageButton,
  tokens,
  children,
  isSlackPreview,
  addBlocksHint
}) => {
  return (
    <AppThemeProviderWithOptionalCustomizations theme={org.experienceTheming}>
      <MessageContainer>
        {infoBox && (
          <MessageFrameInfoBoxContainer>{infoBox}</MessageFrameInfoBoxContainer>
        )}
        {!hidePreview && (
          <>
            <MessageHeader
              logoUrl={
                isSlackPreview
                  ? '/images/integrations-logos/slack.svg'
                  : org.logoUrl
              }
            />
            <MessageMainContainer>
              {addBlocksHint}
              <MessageBody>{children}</MessageBody>
              {!isSlackPreview && (
                <>
                  <MessageFooter1>
                    {hideViewYourPageButton ? (
                      <div />
                    ) : (
                      <ViewYourTasksPageButton />
                    )}
                    <Help
                      tpl={expTpl}
                      subjectFragment={subjectFragment}
                      tokens={tokens}
                    />
                  </MessageFooter1>

                  <MessageSentByDadoFooter />
                </>
              )}
            </MessageMainContainer>
          </>
        )}
      </MessageContainer>
    </AppThemeProviderWithOptionalCustomizations>
  );
};

const SubTaskButtonContainer = styled('div')<{ center?: boolean }>((p) => ({
  display: 'flex',
  justifyContent: p.center ? 'center' : 'left',
  alignItems: 'center',
  gap: 0
}));

const QuestionLabel = styled('label')((p) => ({
  display: 'block',
  fontWeight: p.theme.typography.fontWeightBold
}));

const QuestionRadioContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const FormLabelHeavy = styled(FormLabel)`
  && {
    display: block;
    font-weight: ${(p) => p.theme.typography.fontWeightBold};
    margin-bottom: ${(p) => p.theme.spacing()}px;
  }
`;

const SubtaskLabel = styled('label')<{ hug?: boolean }>((p) => ({
  display: 'block',
  marginTop: p.theme.spacing(p.hug ? 0 : 1),
  fontWeight: p.theme.typography.fontWeightBold
}));

const getPreviousBlockOfASpecificType = (
  bs: TaskBlockDenormalized[],
  type: TaskBlockDenormalized['type']
) => {
  const res: TaskBlockDenormalized[] = [];
  for (let i = bs.length - 1; i >= 0; i--) {
    const b = bs[i];
    if (b.type === type) {
      res.unshift(b);
    } else {
      return res;
    }
  }
  return res;
};

const MultiSubmitButtonContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

const CreateMeetingSubtaskContainer = styled('div')((p) => ({
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius()
}));

const CreateMeetingSubtaskInnerContainer = styled(FlexContainer)((p) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: p.theme.spacing(2),
  gap: p.theme.spacing(2)
}));

const CreateMeetingSubTaskRow = styled(FlexContainer)<{
  additionalTopMargin?: boolean;
}>((p) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1)
}));

const SubtaskCreateCalendarEvent = ({
  b,
  expTpl,
  prevBs,
  nextBs,
  tokens,
  anchorFields
}: {
  b: ISubtaskCreateMeetingDenormalized;
  expTpl: MinimalTaskBuilderContext;
  prevBs: TaskBlockDenormalized[];
  nextBs: TaskBlockDenormalized[];
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  const { title, description, pplToAdd } = b.data;
  const options = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );
  const { tz } = expTpl;
  const suggestedDate = useMemo(() => {
    const anchor =
      options.find((o) => o.type === b.data.anchor.type)?.label || '';
    const onAnchor = capitalize(anchor.toLowerCase().replace('after', 'on'));
    const time = moment()
      .hour(b.data.at.hour)
      .minute(b.data.at.minute)
      .format('hh:mm a');
    const atTime = `at ${time} ${printTz(tz)}`;
    let finalString =
      `${toWeekAndDayDuration(b.data.anchor.lowerBoundary, t)} ${
        b.data.anchor.lowerBoundary === 0 ? onAnchor : anchor
      } ${atTime}` || `${capitalize(anchor)} ${atTime}`;
    finalString = finalString.trim();
    return finalString.length
      ? finalString[0].toUpperCase() + finalString.slice(1)
      : finalString;
  }, [b, options, tz, t]);

  return (
    <CreateMeetingSubtaskContainer>
      <CreateMeetingSubtaskInnerContainer>
        <CreateMeetingSubTaskRow>
          <SubtaskLabel htmlFor={b.id} hug>
            <PlainTextWithReplacementPlaceholders
              text={t('Who should be invited')}
              tokens={tokens}
            />
          </SubtaskLabel>
          <FlexContainer wrap={'wrap'}>
            {pplToAdd.map((p) => (
              <Token color="blue">
                {actionActorToString(p, expTpl.supportingActors, t)}
              </Token>
            ))}
          </FlexContainer>
        </CreateMeetingSubTaskRow>
        <CreateMeetingSubTaskRow>
          <SubtaskLabel htmlFor={b.id} hug>
            <PlainTextWithReplacementPlaceholders
              text={t('Event Title')}
              tokens={tokens}
            />
          </SubtaskLabel>
          <Typography component="p" variant="body2">
            {title}
          </Typography>
        </CreateMeetingSubTaskRow>
        <CreateMeetingSubTaskRow>
          <SubtaskLabel htmlFor={b.id} hug>
            <PlainTextWithReplacementPlaceholders
              text={t('Description')}
              tokens={tokens}
            />
          </SubtaskLabel>
          <Typography component="p" variant="body2">
            {description}
          </Typography>
        </CreateMeetingSubTaskRow>
        <CreateMeetingSubTaskRow>
          <SubtaskLabel htmlFor={b.id} hug>
            <PlainTextWithReplacementPlaceholders
              text={t('Suggested Date')}
              tokens={tokens}
            />
          </SubtaskLabel>
          <span>
            <Typography>{suggestedDate}</Typography>
          </span>
        </CreateMeetingSubTaskRow>
        <CreateMeetingSubTaskRow>
          <FlexContainer>
            {b.data.calendarLinks.map((l) => {
              return (
                <Button
                  key={l.url}
                  variant="outlined"
                  color="primary"
                  onClick={() => {}}
                  endIcon={<ExternalLink size={16} />}
                >
                  {t('Open in {{ service }}', {
                    service: CREATE_MEETING_SERVICES[l.type].name
                  })}
                </Button>
              );
            })}
          </FlexContainer>
          <Typography component="p" variant="caption">
            {t('You can make changes before creating the meeting.')}
          </Typography>
        </CreateMeetingSubTaskRow>
      </CreateMeetingSubtaskInnerContainer>
    </CreateMeetingSubtaskContainer>
  );
};

const SubTaskBlockQuestion = ({
  b,
  prevBs,
  nextBs,
  tokens
}: {
  b: ISubTaskQuestionDenormalized;
  prevBs: TaskBlockDenormalized[];
  nextBs: TaskBlockDenormalized[];
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { question } = b.data;
  const questionLabel = question.label || t('Enter a question...');
  const isNextBlockAQuestion = nextBs[0]?.type === 'QUESTION';
  const previousQuestionBlocks = getPreviousBlockOfASpecificType(
    prevBs,
    SubTaskType.QUESTION
  );
  const isSingleQuestionBlock =
    !isNextBlockAQuestion && !previousQuestionBlocks.length;
  const isLastQuestionBlock =
    !isNextBlockAQuestion && !!previousQuestionBlocks.length;
  const submitButton = (multi?: boolean) => (
    <CompleteButton status={SubTaskStatus.OPEN} mode="PREVIEW">
      {multi ? t('Submit answers') : t('Submit')}
    </CompleteButton>
  );

  switch (question.type) {
    case QuestionType.TEXT:
      const questionPlaceholder = question.placeholder || '';
      const validationType =
        question.validationType || TextQuestionValidationType.NONE;
      const isDateInput = validationType === TextQuestionValidationType.DATE;
      return (
        <>
          <FlexContainer direction="column" spacing={0.5} alignItems="stretch">
            <QuestionLabel htmlFor={b.id}>
              <PlainTextWithReplacementPlaceholders
                text={questionLabel}
                tokens={tokens}
              />
            </QuestionLabel>
            <FlexContainer
              direction={question.rows === 1 ? 'row' : 'column'}
              justifyContent="flex-start"
              alignItems={question.rows === 1 ? 'center' : 'flex-start'}
            >
              <TextField
                defaultValue=""
                InputProps={{
                  readOnly: true,
                  endAdornment: isDateInput ? (
                    <Calendar size={20} style={{ color: '#757575' }} />
                  ) : undefined
                }}
                label=""
                placeholder={questionPlaceholder}
                fullWidth
                multiline={!isDateInput}
                rows={isDateInput ? 1 : question.rows}
              />
              {isSingleQuestionBlock && <div>{submitButton()}</div>}
            </FlexContainer>
          </FlexContainer>
          {isLastQuestionBlock && (
            <MultiSubmitButtonContainer>
              {submitButton(true)}
            </MultiSubmitButtonContainer>
          )}
        </>
      );
    case QuestionType.SELECT:
      return (
        <>
          {question.options.length < 5 ? (
            <>
              <QuestionRadioContainer>
                <FormControl component="fieldset">
                  <FormLabelHeavy>
                    <PlainTextWithReplacementPlaceholders
                      text={questionLabel}
                      tokens={tokens}
                    />
                  </FormLabelHeavy>
                  <RadioGroup name={b.id}>
                    {question.options.map((o) => (
                      <FormControlLabel
                        classes={{
                          root: css({ '&&': { alignItems: 'flex-start' } })
                        }}
                        key={o.id}
                        value={o.id}
                        control={<Radio color="primary" />}
                        label={o.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </QuestionRadioContainer>
              {isSingleQuestionBlock && <div>{submitButton()}</div>}
              {isLastQuestionBlock && (
                <MultiSubmitButtonContainer>
                  {submitButton(true)}
                </MultiSubmitButtonContainer>
              )}
            </>
          ) : (
            <>
              <FlexContainer
                direction="column"
                spacing={0.5}
                alignItems="stretch"
              >
                <QuestionLabel htmlFor={b.id}>
                  <PlainTextWithReplacementPlaceholders
                    text={questionLabel}
                    tokens={tokens}
                  />
                </QuestionLabel>
                <FlexContainer>
                  <Select fullWidth>
                    {question.options.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {isSingleQuestionBlock && <div>{submitButton()}</div>}
                </FlexContainer>
              </FlexContainer>
              {isLastQuestionBlock && (
                <MultiSubmitButtonContainer>
                  {submitButton(true)}
                </MultiSubmitButtonContainer>
              )}
            </>
          )}
        </>
      );
    case QuestionType.MULTI_SELECT:
      return (
        <>
          <QuestionRadioContainer>
            <FormControl component="fieldset">
              <FormLabelHeavy>
                <PlainTextWithReplacementPlaceholders
                  text={questionLabel}
                  tokens={tokens}
                />
              </FormLabelHeavy>
              <FormGroup>
                {question.options.map((o) => (
                  <FormControlLabel
                    key={o.id}
                    value={o.id}
                    control={<Checkbox color="primary" />}
                    label={o.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </QuestionRadioContainer>
          {isSingleQuestionBlock && <div>{submitButton()}</div>}
          {isLastQuestionBlock && (
            <MultiSubmitButtonContainer>
              {submitButton(true)}
            </MultiSubmitButtonContainer>
          )}
        </>
      );
    case QuestionType.ADDRESS:
      return (
        <>
          <FlexContainer direction="column" spacing={0.5} alignItems="stretch">
            {question.label && (
              <FormLabelHeavy htmlFor={b.id}>
                <PlainTextWithReplacementPlaceholders
                  text={questionLabel}
                  tokens={tokens}
                />
              </FormLabelHeavy>
            )}
            <AddressForm
              value={EMPTY_ADDRESS()}
              onChange={NOOP}
              excludedFields={question.excludedFields}
            />

            {isSingleQuestionBlock && <div>{submitButton()}</div>}
            {isLastQuestionBlock && (
              <MultiSubmitButtonContainer>
                {submitButton(true)}
              </MultiSubmitButtonContainer>
            )}
          </FlexContainer>
        </>
      );
    case QuestionType.LINEAR_SCALE:
    case QuestionType.RATING:
      return (
        <>
          <FlexContainer direction="column" spacing={0.5} alignItems="stretch">
            <QuestionLabel htmlFor={b.id}>
              <PlainTextWithReplacementPlaceholders
                text={questionLabel}
                tokens={tokens}
              />
            </QuestionLabel>
            {question.type === QuestionType.RATING ? (
              <RatingInput
                question={question}
                value={null}
                name={b.id}
                id={b.id}
                readOnly
              />
            ) : (
              <LinearScaleInput
                question={question}
                value={null}
                name={b.id}
                id={b.id}
                readOnly
              />
            )}
            {isSingleQuestionBlock && <div>{submitButton()}</div>}
          </FlexContainer>
          {isLastQuestionBlock && (
            <MultiSubmitButtonContainer>
              {submitButton(true)}
            </MultiSubmitButtonContainer>
          )}
        </>
      );
    case QuestionType.SUPPORTING_ACTOR:
      return (
        <>
          <FlexContainer direction="column" spacing={0.5} alignItems="stretch">
            <QuestionLabel htmlFor={b.id}>
              <PlainTextWithReplacementPlaceholders
                text={questionLabel}
                tokens={tokens}
              />
            </QuestionLabel>
            <FlexContainer
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              <TextField
                defaultValue=""
                InputProps={{ readOnly: true }}
                placeholder={t('Enter their work email address')}
                label=""
                fullWidth
                multiline
                rows={1}
              />
              {isSingleQuestionBlock && <div>{submitButton()}</div>}
            </FlexContainer>
          </FlexContainer>
          {isLastQuestionBlock && (
            <MultiSubmitButtonContainer>
              {submitButton(true)}
            </MultiSubmitButtonContainer>
          )}
        </>
      );
  }
};

const SubTaskBlockSwagUpChoice = ({
  value
}: {
  value: ISubTaskSwagUpChoiceDenormalized;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      {value.data.question.choices.length ? (
        <SwagUpChoiceSelector
          value={EMPTY_ARR}
          onChange={() => null}
          options={value.data.question.choices}
          hidePhotos={value.data.question.opts.hidePhotos}
        />
      ) : (
        <FormRow>{t('Choose some swag...')}</FormRow>
      )}
      <AddressExtendedForm
        value={EMPTY_EXTENDED_ADDRESS()}
        onChange={() => null}
      />
      <SubTaskButtonContainer>
        <CompleteButton status={SubTaskStatus.OPEN} mode="PREVIEW">
          {t('Submit choice')}
        </CompleteButton>
      </SubTaskButtonContainer>
    </>
  );
};

const DateFieldChangeSubtaskContainer = styled('div')((p) => ({
  borderColor: p.theme.palette.grey[200],
  borderStyle: 'solid',
  borderWidth: '1px',
  borderRadius: p.theme.custom.border.radius(),
  display: 'flex',
  flexDirection: 'column',
  padding: p.theme.spacing(3),
  gap: p.theme.spacing(2)
}));

const DateFieldChangeSubtaskNote = styled('div')((p) => ({
  paddingTop: p.theme.spacing(1),
  color: p.theme.palette.grey[400],
  fontSize: p.theme.custom.fontSize.s
}));

const SubTaskDateFieldChange = ({
  s,
  status,
  tokens
}: {
  s: ISubTaskDataFieldChangeDenormalized;
  status: SubTaskStatus;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <DateFieldChangeSubtaskContainer>
      <div>
        <ColoredChipLight
          variant="squared"
          color={status === SubTaskStatus.OPEN ? 'yellow' : 'green'}
        >
          {' '}
          {status === SubTaskStatus.OPEN ? 'Not Completed' : 'Completed'}
        </ColoredChipLight>
      </div>
      <MarkdownTextWithReplacementPlaceholders
        t={s.data.instructions}
        throttleMs={500}
        tokens={tokens}
      />

      <DateFieldChangeSubtaskNote>
        {t('Note this can take serveral days to appear marked as done in Dado')}
      </DateFieldChangeSubtaskNote>
    </DateFieldChangeSubtaskContainer>
  );
};

const SubTaskDocuSignEnvelopeSign = ({
  s,
  status,
  tokens
}: {
  s: ISubTaskDocuSignEnvelopeSignDenormalized;
  status: SubTaskStatus;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <FlexContainerVertical fullWidth spacing={1}>
      {s.data.description.t && (
        <MarkdownTextWithReplacementPlaceholders
          t={s.data.description}
          throttleMs={500}
          tokens={tokens}
        />
      )}
      <FlexContainer>
        <strong>{`${t('Signature status')}:`}</strong>
        <ColoredChipLight
          variant="rounded"
          size="small"
          color={
            status === SubTaskStatus.DONE
              ? 'green'
              : status === SubTaskStatus.SKIPPED
              ? 'gray'
              : 'blue'
          }
        >
          {status === SubTaskStatus.DONE
            ? t('Completed')
            : status === SubTaskStatus.SKIPPED
            ? t('Skipped')
            : t('To Do')}
        </ColoredChipLight>
      </FlexContainer>
      <Typography variant="caption">
        {t(
          'This status will automatically update once you have accepted the invitation.'
        )}
      </Typography>
    </FlexContainerVertical>
  );
};


/*
@guide Create a new subtask - 4.1 Create Preview component

Within the TaskBuilder we preview what subtask will look like within
the context of a task. This representation is very similar to what it
will look like inside of an email.

Should also show an error because of the assertNever switch.
*/

// Docebo tenant-homepage link for the LMS subtask email preview, styled like the
// other CompleteLink CTAs (e.g. Calendly). The subdomain isn't populated by the
// client-side (preview) denormalizer, so we read it live from the org's Docebo
// integration config, falling back to any server-stitched value on the block.
// Split so the `useDoceboConfig` listener only mounts once we have a valid user.
const DoceboHomepageCompleteLinkInner = ({
  orgId,
  userId,
  subdomainOverride
}: {
  orgId: string;
  userId: string;
  subdomainOverride?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [doceboConfig] = useDoceboConfig(orgId, userId);
  const subdomain = subdomainOverride || doceboConfig?.data.subdomain;
  if (!subdomain) {
    return null;
  }
  return (
    <SubTaskButtonContainer>
      <CompleteLink
        href={`https://${subdomain}.docebosaas.com/learn/mycourses`}
        startIcon={
          <img
            src="/images/integrations-logos/docebo-tiny-logo.svg"
            alt=""
            width={16}
            height={16}
            style={{ objectFit: 'contain' }}
          />
        }
        status={SubTaskStatus.OPEN}
        mode="LIVE"
        customStyles={{
          padding: '10px 1px'
        }}
      >
        {t('Open your Docebo homepage')}
      </CompleteLink>
    </SubTaskButtonContainer>
  );
};

const DoceboHomepageCompleteLink = ({
  subdomainOverride
}: {
  subdomainOverride?: string;
}) => {
  const currentUser = useCurrentUser();
  if (!currentUser) {
    return null;
  }
  return (
    <DoceboHomepageCompleteLinkInner
      orgId={currentUser.org.id}
      userId={currentUser.id}
      subdomainOverride={subdomainOverride}
    />
  );
};

const SubTaskBlock = ({
  b,
  expTpl,
  prevBs,
  nextBs,
  tokens,
  anchorFields,
  centerButtons
}: {
  b: SubTaskDenormalized;
  expTpl: MinimalTaskBuilderContext;
  prevBs: TaskBlockDenormalized[];
  nextBs: TaskBlockDenormalized[];
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  // Highly descriptive, as we're really only doing this for buttons atm
  centerButtons?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  // if (b.parentLayoutElement) {
  //   return null;
  // }
  switch (b.type) {
    case SubTaskType.CONFIRM:
      return (
        <SubTaskButtonContainer center={centerButtons}>
          <CompleteButton status={SubTaskStatus.OPEN} mode="PREVIEW">
            {b.data.label || `${t('Enter a button label')}...`}
          </CompleteButton>
        </SubTaskButtonContainer>
      );
    case SubTaskType.MEET:
      return (
        <SubTaskButtonContainer>
          <CompleteButton
            startIcon={<Check size={16} />}
            status={SubTaskStatus.OPEN}
            mode="PREVIEW"
          >
            {t(
              b.data.role === 'MANAGER'
                ? 'subTask.meet.confirm.manager'
                : b.data.role === 'MENTOR'
                ? 'subTask.meet.confirm.mentor'
                : b.data.role === 'SKIP_LEVEL_MANAGER'
                ? 'subTask.meet.confirm.skipLevelManager'
                : b.data.role === 'HR'
                ? 'subTask.meet.confirm.hr'
                : b.data.role === 'TEAM'
                ? 'subTask.meet.confirm.team'
                : assertNever(b.data.role)
            )}
          </CompleteButton>
        </SubTaskButtonContainer>
      );
    case SubTaskType.CREATE_MEETING:
      return (
        <SubtaskCreateCalendarEvent
          b={b}
          expTpl={expTpl}
          anchorFields={anchorFields}
          prevBs={prevBs}
          nextBs={nextBs}
          tokens={tokens}
        />
      );
    case SubTaskType.SUBMIT:
      return (
        <SubTaskButtonContainer>
          <CompleteButton
            startIcon={<Upload size={16} />}
            status={SubTaskStatus.OPEN}
            mode="PREVIEW"
          >
            {capitalize(t('subTask.submit.verb'))} {b.data.label || t('file')}
          </CompleteButton>
        </SubTaskButtonContainer>
      );
    case SubTaskType.WATCH:
      return <VideoPlayer url={b.data.url} />;

    case SubTaskType.VISIT:
      return (
        <SubTaskButtonContainer>
          <CompleteLink
            href={b.data.url}
            status={SubTaskStatus.OPEN}
            startIcon={<LinkIcon size={16} strokeWidth={2.75} />}
            mode="PREVIEW"
          >
            {b.data.label ||
              truncateUrl(b.data.url, 52) ||
              'Enter a Link URL...'}
          </CompleteLink>
          {b.status === SubTaskStatus.OPEN && (
            <ColoredChipLight size="small" color="yellow">
              {t('subTask.visit.notVisited')}
            </ColoredChipLight>
          )}
        </SubTaskButtonContainer>
      );
    case SubTaskType.QUESTION:
      return (
        <SubTaskBlockQuestion
          b={b}
          prevBs={prevBs}
          nextBs={nextBs}
          tokens={tokens}
        />
      );
    case SubTaskType.CALENDLY_INVITE:
      return (
        <SubTaskButtonContainer>
          <CompleteLink
            href={b.data.event?.schedulingUrl || ''}
            startIcon={<Calendar size={16} strokeWidth={2.75} />}
            status={SubTaskStatus.OPEN}
            mode="LIVE"
          >
            {t('subTask.calendlyInvite.verb', {
              eventName: b.data.event?.name || t('an event')
            })}
          </CompleteLink>
        </SubTaskButtonContainer>
      );
    case SubTaskType.DOCUMENT_READ:
      return (
        <DocumentTeaserWithPreview
          title={b.data.title}
          contentType={b.data.contentType}
          pages={b.data.pages}
          sizeInBytes={b.data.sizeInBytes}
          thumbnailUrl={b.data.thumbnailUrl}
          docUrl={b.data.docUrl}
        />
      );
    case SubTaskType.SWAG_UP_CHOICE:
      return <SubTaskBlockSwagUpChoice value={b} />;
    case SubTaskType.DATA_FIELD_CHANGE:
      return (
        <SubTaskDateFieldChange
          s={b}
          status={SubTaskStatus.OPEN}
          tokens={tokens}
        />
      );
    case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
      return (
        <SubTaskDocuSignEnvelopeSign
          s={b}
          status={SubTaskStatus.OPEN}
          tokens={tokens}
        />
      );
    case SubTaskType.BACKGROUND_CHECK_INVITE:
      return (
        <SubTaskBackgroundCheckInvite
          s={b}
          status={SubTaskStatus.OPEN}
          mode="PREVIEW"
        />
      );
    case SubTaskType.COMPLETE_LMS_MODULE:
      return (
        <FlexContainerVertical fullWidth spacing={1}>
          {b.data.description.t && (
            <MarkdownTextWithReplacementPlaceholders
              t={b.data.description}
              throttleMs={500}
              tokens={tokens}
            />
          )}
          <FlexContainer>
            <strong>{`${t('Course')}:`}</strong>
            <span>{b.data.unit.name || '—'}</span>
          </FlexContainer>
          <DoceboHomepageCompleteLink subdomainOverride={b.data.subdomain} />
          {b.data.supportContact?.email && (
            <Typography variant="caption" color="textSecondary">
              {t('Something not working? Contact')}{' '}
              <a
                href={`mailto:${b.data.supportContact.email}`}
                style={{ color: 'inherit' }}
              >
                {b.data.supportContact.email}
              </a>
            </Typography>
          )}
          <FlexContainer>
            <strong>{`${t('Status')}:`}</strong>
            <ColoredChipLight variant="rounded" size="small" color="blue">
              {t('To Do')}
            </ColoredChipLight>
          </FlexContainer>
          <Typography variant="caption">
            {t(
              'This status will automatically update once you complete the course in your LMS.'
            )}
          </Typography>
        </FlexContainerVertical>
      );

    // <div> Moin2: {b.data.instructions.t}</div>; //placeholder
    default:
      return assertNever(b);
  }
};

const LayoutBlockComponent = ({
  b,
  expTpl,
  prevBs,
  nextBs,
  tokens,
  allBlocks,
  anchorFields
}: {
  b: LayoutBlock;
  expTpl: MinimalTaskBuilderContext;
  prevBs: TaskBlockDenormalized[];
  nextBs: TaskBlockDenormalized[];
  allBlocks: TaskBlockDenormalized[];
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
}) => {
  switch (b.type) {
    case LayoutBlockType.COLUMNS:
      return (
        <ColumnsContainer
          columns={b.data.columns.length}
          columnWeights={b.data.columns.map((c) => c.columnWeight)}
        >
          {b.data.columns.map((c) => {
            const block = allBlocks.find((b) => b.id === c.blockId);
            if (block && isValidColumnContent(block)) {
              if (isContentBlock(block)) {
                return (
                  <ContentBlock key={c.blockId} b={block} tokens={tokens} />
                );
              }

              if (isSubTaskBlock(block)) {
                return (
                  <SubTaskBlock
                    b={block}
                    prevBs={prevBs}
                    nextBs={nextBs}
                    tokens={tokens}
                    expTpl={expTpl}
                    anchorFields={anchorFields}
                    centerButtons
                  />
                );
              }
            }
            return null;
          })}
        </ColumnsContainer>
      );
  }
};
const EmptyBlock = styled('div')<{ height?: number }>((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  height: p.height || 50,
  color: p.theme.custom.colors.gray.w400
}));

const EmptyTextBlock = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w400
}));

const ContentBlock = ({
  b,
  tokens
}: {
  b: ContentTemplate;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  // if (b.parentLayoutElement) {
  //   return null;
  // }
  switch (b.type) {
    case ContentType.HEADING:
      const sizeVariant = b.data.size === 'L' ? 'h3' : 'h5';
      if (!b.data.t) {
        return (
          <EmptyTextBlock>
            <Typography variant={sizeVariant} component={sizeVariant}>
              {t('Enter a heading')}...
            </Typography>
          </EmptyTextBlock>
        );
      }
      return (
        <Typography variant={sizeVariant} component={sizeVariant}>
          <PlainTextWithReplacementPlaceholders
            text={b.data.t}
            tokens={tokens}
            textAlign={b.data.textAlign}
          />
        </Typography>
      );
    case ContentType.TEXT:
      if (!b.data.t.t) {
        return <EmptyTextBlock>{t('Enter some text')}...</EmptyTextBlock>;
      }
      return (
        <MarkdownTextWithReplacementPlaceholders
          t={b.data.t}
          throttleMs={500}
          tokens={tokens}
        />
      );
    case ContentType.CALLOUT:
      return (
        <CalloutContainer color={b.data.color}>
          {b.data.t ? (
            <MarkdownTextWithReplacementPlaceholders
              t={b.data.t}
              throttleMs={500}
              tokens={tokens}
            />
          ) : (
            <EmptyTextBlock>{t('Enter some text')}...</EmptyTextBlock>
          )}
        </CalloutContainer>
      );

    case ContentType.IMAGE:
      if (!b.data.src) {
        return (
          <EmptyBlock height={100}>
            <Image size={20} />
          </EmptyBlock>
        );
      }
      return (
        <ImageContainer align={b.data.align} width={b.data.width}>
          <Img
            alt={b.data.alt}
            title={b.data.alt}
            src={b.data.src}
            key={b.data.src}
            crossorigin="anonymous"
            unloader={
              <EmptyBlock style={{ width: '100%' }} height={100}>
                <AlertCircle size={20} />
                <div>{t('Image not found')}</div>
              </EmptyBlock>
            }
            loader={
              <EmptyBlock style={{ width: '100%' }} height={100}>
                <CircularProgress color="inherit" />
              </EmptyBlock>
            }
          />
        </ImageContainer>
      );
    case ContentType.DIVIDER:
      return <Divider light={b.data.variant === 'light'} />;
    default:
      return assertNever(b);
  }
};

const Block = ({
  b,
  prevBs,
  nextBs,
  tokens,
  expTpl,
  anchorFields,
  allBlocks
}: {
  b: TaskBlockDenormalized;
  allBlocks: TaskBlockDenormalized[];
  prevBs: TaskBlockDenormalized[];
  nextBs: TaskBlockDenormalized[];
  tokens: TokenDefinition[];
  expTpl: MinimalTaskBuilderContext;
  anchorFields: AnchorField[];
}) => {
  if (isLayoutBlock(b)) {
    return (
      <LayoutBlockComponent
        b={b}
        allBlocks={allBlocks}
        prevBs={prevBs}
        nextBs={nextBs}
        tokens={tokens}
        expTpl={expTpl}
        anchorFields={anchorFields}
      />
    );
  }

  if (isContentBlock(b)) {
    return <ContentBlock b={b} tokens={tokens} />;
  }
  if (isSubTaskBlock(b)) {
    return (
      <SubTaskBlock
        b={b}
        prevBs={prevBs}
        nextBs={nextBs}
        tokens={tokens}
        expTpl={expTpl}
        anchorFields={anchorFields}
      />
    );
  }

  return assertNever(b);
};

const BlockDeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithTypography>
        {t('Delete block?')}
      </DialogTitleWithTypography>
      <DialogContent>
        {t(`The block will be removed immediately. This can't be undone.`)}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Back to editing')}
        </Button>
        <Button color="secondary" variant="contained" onClick={onConfirm}>
          {t('Delete block')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * NEW_UX per-block "assign to segment" control — a person-add icon that opens a
 * dropdown of audiences (color dot + name + checkmark); toggling adds/removes the
 * block from that audience's `blocks[]` (the model's block↔audience membership).
 */
// Prototype ShareTab uses EIcon.UserRoundArrowLeft for the per-block assign
// affordance (proto-app.jsx 506); lucide-react in this repo does not export it,
// so render the same glyph inline.
const UserRoundArrowLeftIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="10" cy="8" r="5" />
    <path d="M2 21a8 8 0 0 1 10.821-7.487" />
    <path d="M16 18h6" />
    <path d="m19 15-3 3 3 3" />
  </svg>
);

export const BlockAudienceAssign: React.FC<{
  blockId: string;
  audiences: Audience[];
  onToggle: (blockId: string, audienceId: string) => void;
  newUx?: boolean;
}> = ({ blockId, audiences, onToggle, newUx }) => {
  const { t } = useUnsafeTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const ordered = [
    ...audiences.filter((a) => a.rule === 'EVERYONE'),
    ...audiences.filter((a) => a.rule !== 'EVERYONE')
  ];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <DragDropItemControlsButton
        newUx={newUx}
        aria-label={t('Assign to segment')}
        title={t('Assign to segment')}
        onClick={() => setOpen((o) => !o)}
      >
        <UserRoundArrowLeftIcon size={12} /> <div>{t('Assign')}</div>
      </DragDropItemControlsButton>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 0,
            left: 'calc(100% + 6px)',
            minWidth: 220,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.06)',
            padding: 6,
            zIndex: 40
          }}
        >
          {ordered.map((a) => {
            const checked = a.blocks.includes(blockId);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onToggle(blockId, a.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  width: '100%',
                  height: 32,
                  padding: '0 9px',
                  border: 0,
                  background: 'transparent',
                  borderRadius: 5,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 400,
                  color: '#1F2937',
                  textAlign: 'left'
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: 2,
                    background: a.color || '#6B7280',
                    flex: 'none'
                  }}
                />
                <span style={{ flex: 1 }}>
                  {getAudienceName(a, audiences.length > 1, t)}
                </span>
                {checked && <Check size={14} style={{ color: '#2563EB' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * NEW_UX read-out strip of the segments a block belongs to (prototype:
 * per-block SegmentChips). Shown only once user segments exist. Each chip is
 * tinted with the segment color; the ✕ unassigns the block from that segment
 * (toggles audience.blocks[]). Condenses to color-only chips beyond 3. The add
 * affordance stays in the hover controls (BlockAudienceAssign).
 */
export const BlockSegmentChips: React.FC<{
  blockId: string;
  audiences: Audience[];
  onToggle: (blockId: string, audienceId: string) => void;
}> = ({ blockId, audiences, onToggle }) => {
  const { t } = useUnsafeTranslation();
  const segments = audiences.filter((a) => a.rule !== 'EVERYONE');
  if (segments.length === 0) return null;
  const assigned = segments.filter((a) => a.blocks.includes(blockId));
  const condensed = assigned.length > 3;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        marginBottom: 6
      }}
    >
      {assigned.length === 0 ? (
        <span
          style={{
            fontSize: 11,
            fontStyle: 'italic',
            fontWeight: 400,
            color: '#C0C4CC'
          }}
        >
          {t('No segment — no one receives this block')}
        </span>
      ) : (
        assigned.map((a) => {
          const seg = a.color || '#6B7280';
          const name = getAudienceName(a, audiences.length > 1, t);
          return condensed ? (
            <span
              key={a.id}
              title={name}
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: seg,
                flex: 'none'
              }}
            />
          ) : (
            <span
              key={a.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 6px 3px 9px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                color: seg,
                background: `${seg}1A`,
                flex: 'none'
              }}
            >
              {name}
              <button
                type="button"
                aria-label={t('Remove from segment')}
                onClick={() => onToggle(blockId, a.id)}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'inherit',
                  display: 'flex',
                  padding: 0,
                  opacity: 0.65
                }}
              >
                <X size={10} />
              </button>
            </span>
          );
        })
      )}
    </div>
  );
};

const BlockItem: React.FC<{
  b: TaskBlockDenormalized;
  selected: boolean;
  // NEW_UX: whether ANY block is currently selected — once one is, hovering others
  // must not reveal their controls (only clicking does).
  anySelected?: boolean;
  error: boolean;
  dragging: boolean;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlock?: (toDelete: string) => void;
  multiSelect: boolean;
  disabled?: boolean;
  onToggleBlockEnabled?: (enabled: boolean) => void;
  onDuplicateBlock?: (blockId: string) => void;
  children: React.ReactNode;
  flags?: React.ReactNode[];
  newUx?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
  // NEW_UX: the currently-focused real segment id (null on Everyone/all-blocks). When
  // set, Delete offers "for all" vs "just for this segment".
  focusedAudienceId?: string | null;
}> = ({
  b,
  selected,
  anySelected,
  error,
  dragging,
  onSelectBlock,
  onDeleteBlock,
  onToggleBlockEnabled,
  onDuplicateBlock,
  children,
  disabled,
  multiSelect,
  flags,
  newUx,
  dragHandleProps,
  assignAudiences,
  onToggleBlockAudience,
  focusedAudienceId
}) => {
  const { t } = useUnsafeTranslation();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const [
    deleteMenuAnchor,
    setDeleteMenuAnchor
  ] = React.useState<HTMLElement | null>(null);
  if (!isLayoutBlock(b) && b.parentLayoutElement) {
    return null;
  }
  // NEW_UX: on a segment tab, Delete is a two-option menu (whole item vs this segment);
  // elsewhere it's a single delete → confirmation dialog.
  const segmentScopedDelete = !!(
    newUx &&
    focusedAudienceId &&
    onToggleBlockAudience
  );
  return (
    <>
      <DragDropItemContainer
        role="button"
        onClick={
          onSelectBlock
            ? (ev) => {
                // stopPropagation works in conjunction with a click handler on the outside,
                // which deselects items on click.
                // we need this guard to make the items itself clickable
                ev.stopPropagation();
                if (disabled) return;
                // NEW_UX: clicking the already-selected block deselects it (closes its
                // settings); classic keeps its select-only behaviour.
                if (newUx && selected && !multiSelect) {
                  onSelectBlock(null);
                } else if (multiSelect || !selected) {
                  onSelectBlock(b.id);
                }
              }
            : undefined
        }
        selected={selected}
        anySelected={anySelected}
        disabled={disabled}
        multiSelect={multiSelect}
        error={error}
        dragging={dragging}
        newUx={newUx}
      >
        <div style={{ pointerEvents: 'none' }}>{children}</div>
        {(onDeleteBlock ||
          onToggleBlockEnabled ||
          onDuplicateBlock ||
          (newUx && assignAudiences && onToggleBlockAudience)) && (
          <>
            <DragDropItemControlsContainer
              className={CONTROLS_CONTAINER_CLASS_NAME}
              newUx={newUx}
              // NEW_UX: controls act on the block directly — clicking one must not bubble
              // to the block's select handler (which would open Block Settings). Classic
              // keeps its previous bubbling behaviour untouched.
              onClick={newUx ? (ev) => ev.stopPropagation() : undefined}
            >
              {newUx && assignAudiences && onToggleBlockAudience && (
                <BlockAudienceAssign
                  blockId={b.id}
                  audiences={assignAudiences}
                  onToggle={onToggleBlockAudience}
                  newUx={newUx}
                />
              )}
              {onToggleBlockEnabled && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={() => {
                    return onToggleBlockEnabled(!!disabled);
                  }}
                >
                  {disabled && (
                    <>
                      <Eye size={12} /> <div>{t('Show block')}</div>
                    </>
                  )}
                  {!disabled && (
                    <>
                      <EyeOff size={12} /> <div>{t('Hide block')}</div>
                    </>
                  )}
                </DragDropItemControlsButton>
              )}
              {onDuplicateBlock && !isLayoutBlock(b) && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={() => onDuplicateBlock(b.id)}
                >
                  <Copy size={12} /> <div>{t('Duplicate')}</div>
                </DragDropItemControlsButton>
              )}
              {newUx && dragHandleProps && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  // rbd drag handle — grab here to reorder blocks.
                  {...dragHandleProps}
                  aria-label={t('Reorder')}
                  title={t('Reorder')}
                  style={{ cursor: 'grab' }}
                >
                  <GripVertical size={12} /> <div>{t('Reorder')}</div>
                </DragDropItemControlsButton>
              )}
              {onDeleteBlock && (
                <DragDropItemControlsButton
                  newUx={newUx}
                  onClick={
                    segmentScopedDelete
                      ? (ev) => setDeleteMenuAnchor(ev.currentTarget)
                      : openDialog
                  }
                >
                  <Trash2 size={12} /> <div>{t('Delete')}</div>
                </DragDropItemControlsButton>
              )}
            </DragDropItemControlsContainer>
            {onDeleteBlock && segmentScopedDelete && (
              <Menu
                anchorEl={deleteMenuAnchor}
                open={!!deleteMenuAnchor}
                onClose={() => setDeleteMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem
                  onClick={() => {
                    setDeleteMenuAnchor(null);
                    openDialog();
                  }}
                >
                  {t('Delete for all')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setDeleteMenuAnchor(null);
                    onToggleBlockAudience?.(b.id, focusedAudienceId!);
                  }}
                >
                  {t('Delete just for this segment')}
                </MenuItem>
              </Menu>
            )}
            {onDeleteBlock && (
              <BlockDeleteConfirmationDialog
                open={dialogOpen}
                onClose={closeDialog}
                onConfirm={() => onDeleteBlock(b.id)}
              />
            )}
          </>
        )}
        {flags && (
          <DragDropItemFlagsContainer>
            {flags.map((flag, index) => (
              <React.Fragment key={index}>{flag}</React.Fragment>
            ))}
          </DragDropItemFlagsContainer>
        )}
      </DragDropItemContainer>
    </>
  );
};

type BlockListProps = {
  bs: TaskBlockDenormalized[];
  expTpl: MinimalTaskBuilderContext;
  selectedBlock: string | null;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlocks?: (blockIds: string[]) => void;
  onDuplicateBlock?: (blockId: string) => void;
  emptyState?: React.ReactNode;
  validation: TaskBuilderBlockValidationState;
  draggable: boolean;
  blockPicker?: {
    picked: string[];
    onUpdate: (blocks: string[]) => void;
  };
  flags?: { [blockId: string]: React.ReactNode[] };
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  // NEW_UX: when provided, replaces a block's read-only render with an inline editor
  // (returns null to keep the read-only render). The shell owns the editor + template.
  renderInlineEditor?: (blockId: string) => React.ReactNode;
  newUx?: boolean;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
  focusedAudienceId?: string | null;
};

export const BlockList = ({
  bs,
  expTpl,
  selectedBlock,
  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlock,
  emptyState,
  validation,
  draggable,
  blockPicker,
  flags,
  tokens,
  anchorFields,
  renderInlineEditor,
  newUx,
  assignAudiences,
  onToggleBlockAudience,
  focusedAudienceId
}: BlockListProps) => {
  return (
    <Droppable droppableId={DROP_IDS.blockOrder} isDropDisabled={false}>
      {(provided, snapshot) => (
        <DragDropListContainer
          highlight={snapshot.isDraggingOver}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          {bs.length
            ? bs.map((b, i) => {
                const inlineEditor = renderInlineEditor?.(b.id);
                return isLayoutBlock(b) || !b.parentLayoutElement ? (
                  <Draggable
                    key={b.id}
                    draggableId={b.id}
                    index={i}
                    // NEW_UX drags via a dedicated grip handle in the block controls
                    // (so click-to-edit doesn't start a drag); classic drags the whole
                    // block. When inline-editing in classic, disable dragging so the
                    // editor stays interactive.
                    isDragDisabled={!draggable || (!newUx && !!inlineEditor)}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...(newUx ? {} : provided.dragHandleProps)}
                      >
                        <BlockItem
                          b={b}
                          selected={selectedBlock === b.id}
                          anySelected={selectedBlock !== null}
                          error={validation[b.id]?.error || false}
                          disabled={
                            !!(
                              blockPicker && !blockPicker.picked.includes(b.id)
                            )
                          }
                          multiSelect={false}
                          dragging={snapshot.isDragging}
                          newUx={newUx}
                          assignAudiences={assignAudiences}
                          onToggleBlockAudience={onToggleBlockAudience}
                          focusedAudienceId={focusedAudienceId}
                          dragHandleProps={
                            newUx ? provided.dragHandleProps : undefined
                          }
                          onSelectBlock={
                            onSelectBlock &&
                            ((x) => {
                              onSelectBlock(x);
                            })
                          }
                          onToggleBlockEnabled={
                            blockPicker
                              ? (nextEnabled) => {
                                  if (b.type === LayoutBlockType.COLUMNS) {
                                    const children = b.data.columns.map(
                                      (c) => c.blockId
                                    );
                                    blockPicker.onUpdate(
                                      nextEnabled
                                        ? [
                                            ...blockPicker.picked,
                                            b.id,
                                            ...children
                                          ]
                                        : blockPicker.picked.filter(
                                            (x) =>
                                              x !== b.id &&
                                              !children.includes(x)
                                          )
                                    );
                                  } else {
                                    blockPicker.onUpdate(
                                      nextEnabled
                                        ? [...blockPicker.picked, b.id]
                                        : blockPicker.picked.filter(
                                            (x) => x !== b.id
                                          )
                                    );
                                  }
                                }
                              : undefined
                          }
                          onDeleteBlock={
                            onDeleteBlocks
                              ? (bId) => {
                                  if (isLayoutBlock(b)) {
                                    const childBlocks = bs.filter(
                                      (b) =>
                                        !isLayoutBlock(b) &&
                                        b.parentLayoutElement === bId
                                    );
                                    onDeleteBlocks([
                                      bId,
                                      ...childBlocks.map((x) => x.id)
                                    ]);
                                  } else {
                                    onDeleteBlocks([bId]);
                                  }
                                }
                              : undefined
                          }
                          onDuplicateBlock={onDuplicateBlock}
                          flags={flags?.[b.id]}
                        >
                          {inlineEditor ?? (
                            <Block
                              b={b}
                              expTpl={expTpl}
                              allBlocks={bs}
                              prevBs={bs.slice(0, i)}
                              nextBs={bs.slice(i + 1)}
                              anchorFields={anchorFields}
                              tokens={tokens}
                            />
                          )}
                        </BlockItem>
                      </div>
                    )}
                  </Draggable>
                ) : null;
              })
            : emptyState}
          {!!bs.length && provided.placeholder}
        </DragDropListContainer>
      )}
    </Droppable>
  );
};

const EmptyBlockList = () => {
  const { t } = useUnsafeTranslation();
  return (
    <DragDropListEmpty>{t('Drag content or sub-tasks here')}</DragDropListEmpty>
  );
};

const PerCohortRepeatHint = ({ type }: { type: 'TASK' | 'MSG' }) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <Divider light />
      <FlexContainer marginTop={2}>
        <InfoBox color="gray">
          {t(
            '{{ type }} content will be repeated here for each other participant in the cohort, exactly as shown above.',
            {
              type: type === 'TASK' ? t('Task') : t('Message')
            }
          )}
        </InfoBox>
      </FlexContainer>
    </>
  );
};

const PerCohortUserHeader = () => {
  const { t } = useTranslation();
  return (
    <FlexContainer justifyContent="space-between" marginBottom={2}>
      <ColoredChipLight color={'darkGray'}>
        Participant: Full Name
      </ColoredChipLight>
      <Button variant="outlined" size="small">
        {t('mail.goToSomeoneElsesPage', {
          name: 'First Name'
        })}
      </Button>
    </FlexContainer>
  );
};

export type TaskMessagePreviewProps = {
  task: ITaskDenormalized;
  expTpl: MinimalTaskBuilderContext;
  org: IOrganizationDenormalized;
  actor: string;
  selectedBlock: string | null;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlocks?: (blockIds: string[]) => void;
  onDuplicateBlock?: (blockId: string) => void;
  blockPicker?: {
    picked: string[];
    onUpdate: (blocks: string[]) => void;
  };
  validation: TaskBuilderBlockValidationState;
  draggable: boolean;
  infoBox?: React.ReactNode;
  flags?: { [blockId: string]: React.ReactNode[] };
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  renderInlineEditor?: (blockId: string) => React.ReactNode;
  batchMode: BatchMode;
  hidePreview?: boolean;
  newUx?: boolean;
  // NEW_UX: the view-only rail shell has no "Add blocks" affordance to hint at —
  // suppresses AddBlocksHintBar there.
  readOnly?: boolean;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
  focusedAudienceId?: string | null;
};

export const TaskMessagePreviewStart = ({
  task,
  expTpl,
  org,
  actor,
  selectedBlock,
  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlock,
  validation,
  draggable,
  blockPicker,
  infoBox,
  hidePreview,
  flags,
  tokens,
  batchMode,
  anchorFields,
  renderInlineEditor,
  newUx,
  readOnly,
  assignAudiences,
  onToggleBlockAudience,
  focusedAudienceId
}: TaskMessagePreviewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { color, group, label } = tokens.find(
    (tk) => tk.token === `${PARTICIPANT_ACTOR_KEY}.fullName`
  ) || {
    color: 'blue',
    group: 'Participant',
    label: 'Full Name'
  };

  const isPerCohortMode = batchMode === BatchMode.PER_COHORT;

  return (
    <MessageFrame
      expTpl={expTpl}
      org={org}
      subjectFragment={task.title}
      infoBox={infoBox}
      hidePreview={hidePreview}
      hideViewYourPageButton={isPerCohortMode}
      tokens={tokens}
      addBlocksHint={
        newUx && !readOnly ? (
          <AddBlocksHintBar onEveryoneTab={!focusedAudienceId} />
        ) : undefined
      }
    >
      <Greeting
        tokens={tokens}
        actor={actor}
        salutations={expTpl.orgOverrides?.salutations || {}}
      />
      <p>
        {t('taskStartMessage.preview')}{' '}
        <strong>
          {actor !== PARTICIPANT_ACTOR_KEY && (
            <>
              <Token color={color} mode="light">
                {group} {group && <>:</>} {label}
              </Token>
              's
            </>
          )}{' '}
          {expTpl.title}
        </strong>
        .
      </p>
      <Divider light style={{ marginBottom: theme.spacing(2) }} />

      {isPerCohortMode && <PerCohortUserHeader />}

      <ExperienceTaskHeading>
        <FlexContainer spacing={1} alignItems="center">
          <ColoredChipLight color="blue" size="small">
            {t('toDo')}
          </ColoredChipLight>
          <Typography variant="h5" component="h5">
            {task.title}
          </Typography>
        </FlexContainer>
        <Typography variant="caption">
          {capitalize(t('due'))}{' '}
          <Token color="blue">{t('taskListMessage.taskDeadline')}</Token>
        </Typography>
      </ExperienceTaskHeading>
      <BlockList
        bs={task.blocks}
        expTpl={expTpl}
        selectedBlock={selectedBlock}
        onSelectBlock={onSelectBlock}
        onDeleteBlocks={onDeleteBlocks}
        onDuplicateBlock={onDuplicateBlock}
        emptyState={<EmptyBlockList />}
        validation={validation}
        blockPicker={blockPicker}
        draggable={draggable}
        flags={flags}
        anchorFields={anchorFields}
        tokens={tokens}
        renderInlineEditor={renderInlineEditor}
        newUx={newUx}
        assignAudiences={assignAudiences}
        onToggleBlockAudience={onToggleBlockAudience}
        focusedAudienceId={focusedAudienceId}
      />
      {isPerCohortMode && <PerCohortRepeatHint type={'TASK'} />}
    </MessageFrame>
  );
};

const ExperienceTaskHeading = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: p.theme.spacing(1),
  marginBottom: p.theme.spacing(2)
}));

const ExperienceMessageSubjectHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ExperienceMessageTaskListTeaserContainer = styled('div')((p) => ({
  padding: '16px 0',
  marginTop: p.theme.spacing(2)
}));

const TaskHeaderContainer = styled('div')((p) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: p.theme.spacing(),
  marginBottom: p.theme.spacing()
}));

const TaskHeader = ({
  title,
  status,
  tokens
}: {
  title: string;
  status: TaskStatus;
  tokens: TokenDefinition[];
}) => {
  return (
    <TaskHeaderContainer>
      <TaskStatusChip status={status} color="light" autoWidth />
      <div>
        <Typography variant="h6" component="h6">
          <PlainTextWithReplacementPlaceholders text={title} tokens={tokens} />
        </Typography>
      </div>
    </TaskHeaderContainer>
  );
};

const ExperienceMessageTaskListTeaser = ({
  msg,
  connectedTasks,
  tokens,
  onClick
}: {
  msg: ExperienceTemplateMessage;
  connectedTasks: ITaskTemplate[];
  tokens: TokenDefinition[];
  /** NEW_UX only — opens the task list's settings panel. Classic doesn't pass this,
   * so its teaser stays the plain, non-interactive preview it's always been. */
  onClick?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  /*
  TODO showing expanded preview. Need the denormalized tasks down here
                {status === TaskStatus.RUNNING && msg.taskList.variant === 'expanded' && (
                  <BlockList
                    blocks={task.blocks}
                    selectedBlock={null}
                    validation={{}}
                    draggable={false}
                    tokens={tokens}
                  />
                )}
  */

  // Matches getTaskTemplatesWithSameActorAndWithinRange's own scoping: a trigger-based
  // message only has a meaningful Task List preview for the "same time" option — the
  // other two are date-math options with no absolute date to measure against here.
  if (isEventDrivenTiming(msg.timing) && msg.taskList.range !== 1 * ONE_DAY) {
    return null;
  }

  return (
    <>
      <Divider light />
      <ExperienceMessageTaskListTeaserContainer
        onClick={
          onClick
            ? (e: React.MouseEvent) => {
                e.stopPropagation();
                onClick();
              }
            : undefined
        }
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        {!!connectedTasks.length &&
          connectedTasks.map((task) => {
            const status =
              (task.timing.type === 'fixed' &&
                msg.timing.type === 'fixed' &&
                task.timing.start === msg.timing.start) ||
              templateTimingsShareTriggerCondition(task.timing, msg.timing)
                ? TaskStatus.RUNNING
                : TaskStatus.NOT_STARTED;
            return (
              <>
                <TaskHeader
                  key={task.id}
                  title={task.title}
                  status={status}
                  tokens={tokens}
                />
              </>
            );
          })}

        {!connectedTasks.length && (
          <Centered>
            <Typography variant="caption">
              {t('No tasks start on the selected period')}
            </Typography>
          </Centered>
        )}
      </ExperienceMessageTaskListTeaserContainer>
    </>
  );
};

export const ExperienceMessagePreviewBody = ({
  denormalizedMessage,
  denormalizedOrg,
  infoBox,
  expTpl,

  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlock,
  validation,
  draggable,
  flags,
  wontSend,
  blockPicker,
  selectedBlock,
  tokens,
  anchorFields,
  bottomInfo,
  hideViewYourPageButton,
  showRepeatUserHeader,
  emptyState,
  useSlackHeader,
  newUx,
  readOnly,
  renderInlineEditor,
  assignAudiences,
  onToggleBlockAudience,
  focusedAudienceId
}: {
  denormalizedMessage: IExperienceMessageDenormalized;
  denormalizedOrg: IOrganizationDenormalized;
  infoBox: React.ReactNode;
  expTpl: MinimalTaskBuilderContext;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlocks?: (blockIds: string[]) => void;
  onDuplicateBlock?: (blockId: string) => void;
  validation: TaskBuilderBlockValidationState;
  draggable: boolean;
  flags?: { [key: string]: React.ReactNode[] };
  wontSend?: boolean;
  blockPicker?: BlockListProps['blockPicker'];
  selectedBlock: string | null;
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  bottomInfo?: React.ReactNode;
  hideViewYourPageButton?: boolean;
  showRepeatUserHeader?: boolean;
  emptyState: React.ReactNode;
  useSlackHeader: boolean;
  newUx?: boolean;
  // NEW_UX: the view-only rail shell has no "Add blocks" affordance to hint at —
  // suppresses AddBlocksHintBar there.
  readOnly?: boolean;
  renderInlineEditor?: (blockId: string) => React.ReactNode;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
  focusedAudienceId?: string | null;
}) => {
  return (
    <MessageFrame
      expTpl={expTpl}
      org={denormalizedOrg}
      subjectFragment={denormalizedMessage.subject}
      infoBox={infoBox}
      hidePreview={wontSend}
      hideViewYourPageButton={hideViewYourPageButton}
      tokens={tokens}
      isSlackPreview={useSlackHeader}
      addBlocksHint={
        newUx && !readOnly ? (
          <AddBlocksHintBar onEveryoneTab={!focusedAudienceId} />
        ) : undefined
      }
    >
      {showRepeatUserHeader && <PerCohortUserHeader />}
      <ExperienceMessageSubjectHeading>
        <Typography variant="h5" component="h5">
          <strong>
            <PlainTextWithReplacementPlaceholders
              text={denormalizedMessage.subject}
              tokens={tokens}
            />
          </strong>
        </Typography>
      </ExperienceMessageSubjectHeading>
      <BlockList
        bs={denormalizedMessage.blocks}
        expTpl={expTpl}
        selectedBlock={selectedBlock}
        onSelectBlock={onSelectBlock}
        onDeleteBlocks={onDeleteBlocks}
        onDuplicateBlock={onDuplicateBlock}
        emptyState={emptyState}
        validation={validation}
        draggable={draggable}
        anchorFields={anchorFields}
        flags={flags}
        blockPicker={blockPicker}
        tokens={tokens}
        renderInlineEditor={renderInlineEditor}
        newUx={newUx}
        assignAudiences={assignAudiences}
        onToggleBlockAudience={onToggleBlockAudience}
        focusedAudienceId={focusedAudienceId}
      />
      {bottomInfo}
    </MessageFrame>
  );
};

export const ExperienceMessagePreview = ({
  msg,
  expTpl,
  org,
  selectedBlock,
  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlock,
  validation,
  draggable,
  viewMode,
  dragDropContext,
  onPickBlocks,
  connectedTasks,
  tokens,
  newUx,
  readOnly,
  renderInlineEditor,
  assignAudiences,
  onToggleBlockAudience,
  onClickTaskList
}: {
  msg: ExperienceTemplateMessage;
  expTpl: TaskBuilderContext;
  org: IOrganization;
  selectedBlock: string | null;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlocks?: (blockIds: string[]) => void;
  onDuplicateBlock?: (blockId: string) => void;
  validation: TaskBuilderBlockValidationState;
  draggable: boolean;
  viewMode: MessageBuilderViewMode;
  /** NEW_UX only — see ExperienceMessageTaskListTeaser. */
  onClickTaskList?: () => void;
  dragDropContext?: React.MutableRefObject<{
    currentlyVisibleBlocks: string[];
  }>;
  onPickBlocks?: (blocks: string[]) => void;
  connectedTasks: ITaskTemplate[];
  tokens: TokenDefinition[];
  newUx?: boolean;
  readOnly?: boolean;
  renderInlineEditor?: (blockId: string) => React.ReactNode;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const orgOverrides = expTpl.orgOverrides;
  const denormalizedOrg = useMemo(() => toDenormalizedOrg(org, orgOverrides), [
    org,
    orgOverrides
  ]);
  const orgName = denormalizedOrg.name;
  const dateFormats = denormalizedOrg.dateFormats;
  const { audiences, batchMode } = msg;
  const anchorFields = useAnchorFields(org.customUserFields);
  const audience = useMemo(() => {
    if (viewMode.mode === 'AUDIENCES_DETAIL') {
      return viewMode.editModel;
    }
    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return getAudienceById(audiences, viewMode.previewAudienceId);
    }
    return getEveryoneAudience(audiences);
  }, [viewMode, audiences]);

  const interpolationOptions: InterpolationDefaultOptions = useMemo(() => {
    return {
      dateFormat: dateFormats.MMDDYYYY
    };
  }, [dateFormats.MMDDYYYY]);

  const denormalizedMessage = useMemo(() => {
    const ignoreAudience = !(
      (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) ||
      (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId)
    );

    return toDenormalizedMessage(
      msg,
      audience,
      {
        experience: { vars: { title: expTpl.title, tasks: {} } },
        org: { vars: { name: orgName } },
        roles: []
      },
      MessageStatus.UPCOMING,
      now(),
      interpolationOptions,
      { ignoreAudience, allowEmptyBlocks: true }
    );
  }, [msg, expTpl, orgName, viewMode, audience, interpolationOptions])!; // ignoreAudience always returns a result

  if (dragDropContext) {
    dragDropContext.current = {
      currentlyVisibleBlocks: denormalizedMessage.blocks.map((b) => b.id)
    };
  }

  // NEW_UX removes the "preview" concept entirely: a focused segment tab is just the
  // editable view of that segment, so none of the preview/audience info banners apply.
  const infoBox = newUx ? null : (
    <>
      {viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId &&
      audience.blocks.length === 0 ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              "No visible blocks. This message won't be sent to the **{{ audienceName }}** audience.",
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        viewMode.previewAudienceId ? (
        // NEW_UX: the selected segment is already shown by the segment tabs on top
        // of the preview sheet, so the "This is a preview of X" banner is redundant.
        newUx ? null : (
          <InfoBox>
            <LightMdToJsx>
              {t('This is a preview of the **{{ audienceName }}** audience.', {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              })}
            </LightMdToJsx>
          </InfoBox>
        )
      ) : !newUx &&
        viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        audiences.length > 1 ? (
        <InfoBox>
          {t('Showing all blocks. Select an audience to preview.')}
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_DETAIL' ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              'New blocks will only apply to the audience **{{ audienceName }}**.',
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : null}
    </>
  );

  const totalNumberOfBlocks = msg.blocks.length;

  const flags = useMemo(() => {
    // NEW_UX: per-block audience flags ("E" / "All") are redundant with the segment
    // tabs and the per-block Assign control, so the new editor omits them.
    if (newUx) {
      return undefined;
    }
    if (audiences.length === 1) {
      if (audiences[0].blocks.length === totalNumberOfBlocks) {
        return undefined;
      }
    }

    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return undefined;
    }

    if (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) {
      return undefined;
    }

    const res: { [blockId: string]: React.ReactNode[] } = {};
    audiences.forEach((uneditedAudience) => {
      const a =
        viewMode.mode === 'AUDIENCES_DETAIL' &&
        uneditedAudience.id === viewMode.editModel.id
          ? viewMode.editModel
          : uneditedAudience;
      const name = getAudienceName(a, audiences.length > 1, t);
      const el = (
        <DragDropItemFlag
          key={a.id}
          label={name[0]}
          color={a.color || DEFAULT_AUDIENCE_COLOR}
          tooltip={t('Used by {{ audienceName }} audience', {
            audienceName: name
          })}
        />
      );
      a.blocks.forEach((bId) => {
        const container = (res[bId] = res[bId] || []);
        container.push(el);
      });
    });
    return mapValues(res, (els) => {
      if (els.length === audiences.length && audiences.length > 1) {
        return [
          <DragDropItemFlag
            label={t('All')}
            color={DEFAULT_AUDIENCE_COLOR}
            tooltip={t('Used by all audiences')}
          />
        ];
      }
      return els;
    });
  }, [viewMode, audiences, totalNumberOfBlocks, t, newUx]);

  const blockPicker: BlockListProps['blockPicker'] = useMemo(() => {
    if (onPickBlocks && viewMode.mode === 'AUDIENCES_DETAIL') {
      const audience = viewMode.editModel;
      return {
        picked: audience.blocks,
        onUpdate: onPickBlocks
      };
    }
    return undefined;
  }, [viewMode, onPickBlocks]);

  const wontSend =
    (viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId &&
      audience.blocks.length === 0) ||
    false;

  const showRepeatHint =
    batchMode === BatchMode.PER_COHORT &&
    !approximateIfAggregationForMessageIsPossible(
      msg,
      getAnchorInterpolationVariable(expTpl.anchor, anchorFields)
    );
  const hasSlackChannelActor = () => {
    const actor = expTpl.supportingActors[msg.actor] as
      | ISupportingActor
      | undefined;
    return (
      actor?.type === SupportingActorType.CHANNEL &&
      actor.channeltype === ChannelType.SLACK
    );
  };

  return (
    <ExperienceMessagePreviewBody
      expTpl={expTpl}
      useSlackHeader={hasSlackChannelActor()}
      denormalizedMessage={denormalizedMessage}
      denormalizedOrg={denormalizedOrg}
      infoBox={infoBox}
      wontSend={wontSend}
      tokens={tokens}
      selectedBlock={selectedBlock}
      onSelectBlock={onSelectBlock}
      onDeleteBlocks={onDeleteBlocks}
      onDuplicateBlock={onDuplicateBlock}
      emptyState={
        <DragDropListEmpty>{t('Drag content here')}</DragDropListEmpty>
      }
      validation={validation}
      draggable={draggable}
      anchorFields={anchorFields}
      flags={flags}
      blockPicker={blockPicker}
      hideViewYourPageButton={batchMode !== BatchMode.DEFAULT}
      showRepeatUserHeader={showRepeatHint}
      bottomInfo={
        !!denormalizedMessage.taskList.range &&
        batchMode === BatchMode.DEFAULT ? (
          <ExperienceMessageTaskListTeaser
            msg={msg}
            connectedTasks={connectedTasks}
            tokens={tokens}
            onClick={onClickTaskList}
          />
        ) : showRepeatHint ? (
          <PerCohortRepeatHint type="MSG" />
        ) : null
      }
      newUx={newUx}
      readOnly={readOnly}
      renderInlineEditor={renderInlineEditor}
      assignAudiences={assignAudiences}
      onToggleBlockAudience={onToggleBlockAudience}
      focusedAudienceId={audience.rule !== 'EVERYONE' ? audience.id : null}
    />
  );
};
